import { type AiTagId, type SiteId } from '@solinkcorp/api-types';
import { useMemo } from 'react';
import usePeopleTrackingRawData from './usePeopleTrackingRawData';

export interface VehicleJourneyZoneConfig {
  /** The site has at least one zone flagged as a vehicle-journey start zone. */
  hasStartZone: boolean;
  /** The site has at least one zone flagged as a vehicle-journey end zone. */
  hasEndZone: boolean;
  /**
   * Both a start zone and an end zone exist, i.e. the edge can actually pair a journey.
   * A total-journey-time widget is only meaningful when this is true.
   */
  isConfigured: boolean;
  /**
   * AI tags attached to an end zone. The total-journey-time widget is driven by the
   * end-zone exit event (that's where the edge attaches `vehicle_journey`), so these are
   * the only tags that can produce a value. The widget dialog currently offers all AI tags
   * but only these are relevant.
   * Work will have to be done to filter the dialog's tag list to only show these tags, or
   * at least indicate which are relevant in the future.
   */
  endZoneAiTagIds: AiTagId[];
  /**
   * False while the site's vision analytics config hasn't arrived yet, so callers can
   * avoid flashing a "not configured" error before the fetch resolves.
   */
  isLoaded: boolean;
}

const EMPTY_CONFIG: VehicleJourneyZoneConfig = {
  hasStartZone: false,
  hasEndZone: false,
  isConfigured: false,
  endZoneAiTagIds: [],
  isLoaded: false,
};

/**
 * Reads the site's vehicle-journey zone configuration out of the device's vision analytics
 * config (the same "line crossing configuration (with multicam)" payload behind
 * `useSiteSpecificVisionAnalyticsCamera`).
 *
 * The journey flags live on each `dwell_event_polygons[].metadata` and are only written when
 * true (see `getVehicleJourneyValues` in the zone-tracking config), so an absent flag means
 * false rather than unknown.
 *
 * Keyed by `siteId`, so switching location re-resolves the device and refetches — the widget
 * and its dialog follow the selected site without extra plumbing.
 */
export default function useVehicleJourneyZones(
  siteId: SiteId | string | undefined,
): VehicleJourneyZoneConfig {
  const { rawData } = usePeopleTrackingRawData(siteId);

  return useMemo(() => {
    if (!rawData) return EMPTY_CONFIG;

    let hasStartZone = false;
    let hasEndZone = false;
    const endZoneAiTagIds = new Set<AiTagId>();

    for (const cameraConfig of Object.values(rawData)) {
      for (const polygon of cameraConfig?.['dwell_event_polygons'] ?? []) {
        const metadata = polygon?.metadata as
          | {
              isVehicleJourneyStartZone?: boolean;
              isVehicleJourneyEndZone?: boolean;
              aiTagIds?: string[];
            }
          | undefined;
        if (!metadata) continue;

        if (metadata.isVehicleJourneyStartZone) hasStartZone = true;
        if (metadata.isVehicleJourneyEndZone) {
          hasEndZone = true;
          for (const id of metadata.aiTagIds ?? []) {
            endZoneAiTagIds.add(id as AiTagId);
          }
        }
      }
    }

    return {
      hasStartZone,
      hasEndZone,
      isConfigured: hasStartZone && hasEndZone,
      endZoneAiTagIds: Array.from(endZoneAiTagIds),
      isLoaded: true,
    };
  }, [rawData]);
}
